import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import * as translator from '../../../../../utils/translator';
import { clientConfig } from '../../../../../config';
import { TourStepConfig } from './careProfileTourConfig';
import { TourPhase } from './useCareProfileTour';

const ONBOARDING_STEP1_IMAGE = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/onboarding/onboarding-step1/onboarding-step1.png`;
const ONBOARDING_FINISH_IMAGE = 'https://images.doktorabc.com/redesign_2025/care-plus/onboarding/onboarding-finish/onboarding-finish.png';

interface AnchorRect {
	top: number;
	left: number;
	width: number;
	height: number;
}

interface CareProfileTourProps {
	language: string;
	firstName: string;
	isMonthlyPlan?: boolean;
	phase: TourPhase;
	currentStep: TourStepConfig | null;
	stepIndex: number;
	totalSteps: number;
	onStart: () => void;
	onNext: () => void;
	onBack: () => void;
	onSkip: () => void;
	onComplete: () => void;
}

const TOOLTIP_WIDTH = 320;
const VIEWPORT_PADDING = 16;
const TOOLTIP_GAP = 14;
const POINTER_HEIGHT = 8;

const TOOLTIP_SHADOW = '0px 0px 1px 0px rgba(5,27,55,0.16), 0px 8px 16px 0px rgba(5,27,55,0.08)';

const t = (language: string, path: string): string => translator.translate(language, 'my_account', path) ?? '';

const CareProfileTour: React.FC<CareProfileTourProps> = ({
	language,
	firstName,
	isMonthlyPlan,
	phase,
	currentStep,
	stepIndex,
	totalSteps,
	onStart,
	onNext,
	onBack,
	onSkip,
	onComplete,
}) => {
	const [anchor, setAnchor] = useState<AnchorRect | null>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	// The vertical placement chosen during render, so the safe-area effect below can reset to it
	// before re-measuring instead of compounding its own previous correction.
	const tooltipStyleRef = useRef<React.CSSProperties | null>(null);
	// True when the tooltip had to be pinned over the anchor because the anchor is too tall for it to
	// sit outside; the safe-area effect leaves those placements alone.
	const overlayModeRef = useRef(false);
	const isActive = phase === 'welcome' || phase === 'step';

	const getAnchorEls = useCallback((): HTMLElement[] | null => {
		if (!currentStep?.anchorId) return null;
		const isDesktop = window.innerWidth >= 1024;
		// Some steps render two copies of the same card (a mobile layout copy and a desktop layout copy) with
		// different ids, so on mobile we resolve to the mobile-specific id when one is configured.
		const primaryId = !isDesktop && currentStep.mobileAnchorId ? currentStep.mobileAnchorId : currentStep.anchorId;
		const primary = document.getElementById(primaryId);
		if (!primary) return null;

		// Union the primary anchor with any extra anchors (e.g. a nearby card/button not covered by the
		// primary anchor's own bounding box) to form a wider spotlight, on both mobile and desktop.
		const els: HTMLElement[] = [primary];
		if (currentStep.desktopAnchorIds) {
			currentStep.desktopAnchorIds.forEach((id) => {
				const extra = document.getElementById(id);
				if (extra) els.push(extra);
			});
		}
		return els;
	}, [currentStep?.anchorId, currentStep?.mobileAnchorId, currentStep?.desktopAnchorIds]);

	const measureAnchor = useCallback((): void => {
		const els = getAnchorEls();
		if (!els) {
			setAnchor(null);
			return;
		}
		// Ignore hidden/zero-size elements (e.g. a `display:none` responsive duplicate) so they never
		// drag the spotlight union toward the page origin.
		const rects = els.map((el) => el.getBoundingClientRect()).filter((r) => r.width > 0 && r.height > 0);
		if (rects.length === 0) {
			setAnchor(null);
			return;
		}
		const top = Math.min(...rects.map((r) => r.top));
		const left = Math.min(...rects.map((r) => r.left));
		const right = Math.max(...rects.map((r) => r.right));
		const bottom = Math.max(...rects.map((r) => r.bottom));
		setAnchor({ top, left, width: right - left, height: bottom - top });
	}, [getAnchorEls]);

	// Position the spotlight + tooltip as one centered block. We do a single INSTANT scroll (no smooth
	// animation, so it's invisible to the user) so the anchor sits high/low enough that its tooltip also
	// fits on screen, then measure. After this one jump the page is locked (see the overflow effect), so
	// there's no ongoing scrolling. Retries a few times in case the anchor mounts after the step opens.
	const locateAnchor = useCallback((retryCount = 0): void => {
		const els = getAnchorEls();
		const rects = els?.map((el) => el.getBoundingClientRect()).filter((r) => r.width > 0 && r.height > 0);
		if (!rects || rects.length === 0) {
			if (retryCount < 10) {
				setTimeout(() => locateAnchor(retryCount + 1), 250);
			} else {
				setAnchor(null);
			}
			return;
		}
		const top = Math.min(...rects.map((r) => r.top));
		const bottom = Math.max(...rects.map((r) => r.bottom));
		const anchorHeight = bottom - top;

		const isMobile = window.innerWidth < 640;
		// On desktop a 'left' step renders the tooltip beside the anchor (not below/above), so no vertical
		// reserve is needed — just center the anchor itself. On mobile 'left' falls back to 'above'.
		const sideBySide = currentStep?.placement === 'left' && !isMobile;

		let desiredScroll: number;
		if (sideBySide) {
			const anchorTargetTop = (window.innerHeight - anchorHeight) / 2;
			desiredScroll = window.scrollY + top - anchorTargetTop;
		} else {
			// Reserve room for the tooltip on the side it will render (below for most steps, above for those
			// configured 'above'/mobile-'left'), so the whole block stays inside the viewport. Prefer the
			// tooltip's real rendered height — the body text wraps to a different number of lines per locale
			// and viewport width, so a fixed reserve under-reserves for taller cards and lets them collide
			// with the top edge. Falls back to an estimate only on the very first pass, before it mounts.
			const measuredTooltipHeight = dialogRef.current?.offsetHeight ?? 0;
			const TOOLTIP_RESERVE = measuredTooltipHeight > 0 ? measuredTooltipHeight : isMobile ? 320 : 260;
			const gap = TOOLTIP_GAP + POINTER_HEIGHT + TOOLTIP_RESERVE;
			const placeBelow = currentStep?.placement !== 'above' && currentStep?.placement !== 'left';
			const blockHeight = anchorHeight + gap;
			// Vertically center the [anchor + reserved tooltip] block in the viewport.
			const blockTopInViewport = (window.innerHeight - blockHeight) / 2;
			// When the tooltip is above, the anchor should sit below the reserved space instead.
			let anchorTargetTop = placeBelow ? blockTopInViewport : blockTopInViewport + gap;
			// When the anchor is taller than the space left over (e.g. the long timeline card), centering
			// the block would push the tooltip past the viewport edge. Prioritise the tooltip: keep it fully
			// on screen and let the oversized anchor's far end run off instead.
			if (blockHeight > window.innerHeight) {
				anchorTargetTop = placeBelow ? VIEWPORT_PADDING : gap + VIEWPORT_PADDING;
			}
			desiredScroll = window.scrollY + top - anchorTargetTop;
		}

		document.body.style.overflow = '';
		window.scrollTo({ top: Math.max(desiredScroll, 0), behavior: 'auto' });
		document.body.style.overflow = 'hidden';
		measureAnchor();
	}, [getAnchorEls, measureAnchor, currentStep?.placement]);

	useEffect(() => {
		// Clear any stale anchor from the previous step immediately, so we never render this step's tooltip
		// positioned against the previous step's spotlight while the new anchor is still being measured.
		setAnchor(null);
		if (phase !== 'step') return;
		const settleTimer = setTimeout(() => locateAnchor(), 350);
		const onReflow = (): void => measureAnchor();
		window.addEventListener('resize', onReflow);
		window.addEventListener('scroll', onReflow, true);
		return () => {
			clearTimeout(settleTimer);
			window.removeEventListener('resize', onReflow);
			window.removeEventListener('scroll', onReflow, true);
		};
	}, [phase, locateAnchor, measureAnchor]);

	useEffect(() => {
		if (!isActive) return;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isActive]);

	useEffect(() => {
		if (!isActive) return;
		dialogRef.current?.focus();

		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				event.preventDefault();
				onSkip();
				return;
			}
			if (event.key !== 'Tab') return;
			const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			);
			if (!focusable || focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isActive, onSkip]);

	// Keep the tooltip inside the viewport's vertical safe area. This runs after every render and
	// always rewrites all four properties it owns, because React only re-applies the keys present in
	// `style={tooltipStyle}` — a value written here imperatively would otherwise leak into the next
	// step (e.g. a clamped `top`/`bottom: auto` surviving onto an 'above' step and flipping it).
	// The measured height is used rather than a constant because the translated body text wraps to a
	// different number of lines per locale and viewport width.
	useLayoutEffect(() => {
		const el = dialogRef.current;
		if (!el || !isActive) return;

		// Reset to the placement chosen during render before measuring, so this never compounds on
		// top of a previous pass's correction.
		const desiredTop = tooltipStyleRef.current?.top;
		const desiredBottom = tooltipStyleRef.current?.bottom;
		el.style.top = typeof desiredTop === 'number' ? `${desiredTop}px` : desiredTop ?? '';
		el.style.bottom = typeof desiredBottom === 'number' ? `${desiredBottom}px` : desiredBottom ?? '';
		el.style.maxHeight = '';
		el.style.overflowY = '';

		const available = window.innerHeight - VIEWPORT_PADDING * 2;
		// Cap the height first: a tooltip taller than the safe area can't be fixed by moving it, and
		// would otherwise push its own controls (the X / Back / Next row) under the browser chrome.
		if (el.offsetHeight > available) {
			el.style.maxHeight = `${available}px`;
			el.style.overflowY = 'auto';
		}

		// Overlay placement is already pinned to a viewport edge by design; re-clamping it here would
		// fight that decision (and could drag it back over the anchor).
		if (overlayModeRef.current) return;

		const rect = el.getBoundingClientRect();
		if (rect.top < VIEWPORT_PADDING) {
			el.style.bottom = 'auto';
			el.style.top = `${VIEWPORT_PADDING}px`;
		} else if (rect.bottom > window.innerHeight - VIEWPORT_PADDING) {
			el.style.top = 'auto';
			el.style.bottom = `${VIEWPORT_PADDING}px`;
		}
	});

	if (!isActive) return null;

	const backdropBase = 'fixed inset-0 z-[100000]';

	// The centered welcome/final layout is only for the welcome step and the true final step (no anchorId).
	// While `anchor` is momentarily null for an anchored step (still being located after a step change), we
	// render nothing below instead of falling into this branch — otherwise it would flash the "final" CTA
	// (wired to onComplete) and look like the tour closed itself.
	const isFinalStep = phase === 'step' && !currentStep?.anchorId;
	if (phase === 'welcome' || isFinalStep) {
		const isWelcome = phase === 'welcome';
		const titleKey = isWelcome ? 'CareProfile/Tour/Welcome/Title' : 'CareProfile/Tour/Final/Title';
		const welcomeBodyKey = isMonthlyPlan ? 'CarePlus/monthlyPlan/careProfileWelcomeBody' : 'CareProfile/Tour/Welcome/Body';
		const bodyKey = isWelcome ? welcomeBodyKey : 'CareProfile/Tour/Final/Body';
		const ctaKey = isWelcome ? 'CareProfile/Tour/Welcome/Cta' : 'CareProfile/Tour/Final/Cta';
		const title = t(language, titleKey).replace('{{name}}', firstName || '');
		const body = t(language, bodyKey);
		const cta = t(language, ctaKey);
		const isFinal = isFinalStep;
		const showMobileImage = true;
		const mobileImageSrc = isFinal ? ONBOARDING_FINISH_IMAGE : ONBOARDING_STEP1_IMAGE;

		return createPortal(
			<div className={`${backdropBase} flex items-center justify-center p-4`}>
				<div className="absolute inset-0 bg-black/40" style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }} />
				<div
					ref={dialogRef}
					role="dialog"
					aria-modal="true"
					aria-label={title}
					tabIndex={-1}
					className="relative w-[min(354px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-auto rounded-[20px] bg-white"
					style={{ boxShadow: TOOLTIP_SHADOW }}
				>
					{showMobileImage && (
						<div className={`relative h-[140px] w-full flex items-center justify-center overflow-hidden bg-[#f5f5f5] ${isFinal ? '' : 'sm:hidden'}`}>
							<img
								src={mobileImageSrc}
								alt=""
								className="absolute inset-0 w-full h-full object-cover object-center"
							/>
							<button
								type="button"
								aria-label={t(language, 'CareProfile/Tour/Back')}
								onClick={onSkip}
								className="absolute top-[9px] right-3 flex items-center justify-center w-[31px] h-[31px] rounded-full bg-transparent border-none text-white cursor-pointer"
							>
								<FiX size={19} />
							</button>
						</div>
					)}
					<div className="p-6 flex flex-col gap-y-4">
						<div className={showMobileImage && !isFinal ? 'sm:flex sm:items-start sm:justify-between sm:gap-x-2' : 'flex items-start justify-between gap-x-2'}>
							<div>
								<p className="text-[19px] font-semibold text-care-plus-heading leading-[1.3]">{title}</p>
								<p className="text-sm text-[#616975] mt-2 leading-[1.4]">{body}</p>
							</div>
							{!isFinal && (
								<button
									type="button"
									aria-label={t(language, 'CareProfile/Tour/Back')}
									onClick={onSkip}
									className={`${showMobileImage ? 'hidden sm:flex' : 'flex'} items-center justify-center w-[31px] h-[31px] rounded-full bg-transparent border-none text-[#98a0af] cursor-pointer flex-shrink-0`}
								>
									<FiX size={19} />
								</button>
							)}
						</div>
						{isFinal && totalSteps > 0 && (
							<p className="text-[12px] text-[#98a0af]" aria-live="polite">
								{t(language, 'CareProfile/Tour/Counter').replace('{{current}}', String(totalSteps)).replace('{{total}}', String(totalSteps))}
							</p>
						)}
						<button
							type="button"
							onClick={isWelcome ? onStart : onComplete}
							className="w-full bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover text-white font-medium py-3 px-6 rounded-full border-none cursor-pointer transition-all duration-200"
						>
							{cta}
						</button>
					</div>
				</div>
			</div>,
			document.body,
		);
	}

	// Anchored step whose anchor hasn't been located yet (e.g. just after advancing to a new step) — keep a
	// blocking overlay visible so the page stays non-interactive during the ~350ms transition.
	if (!anchor) {
		return createPortal(
			<div className={backdropBase} style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} />,
			document.body,
		);
	}

	const cutTop = Math.max(anchor!.top - 6, 0);
	const cutLeft = Math.max(anchor!.left - 8, 0);
	const cutWidth = anchor!.width + 16;
	const cutHeight = anchor!.height + 12;
	const cutCenterX = cutLeft + cutWidth / 2;

	const isMobileViewport = window.innerWidth < 640;
	const tooltipWidth = isMobileViewport ? Math.min(window.innerWidth - VIEWPORT_PADDING * 2, cutWidth) : TOOLTIP_WIDTH;

	// Desktop 'left' places the tooltip beside the anchor (to its left, pointer on the right edge). On mobile
	// it falls back to sitting above the card.
	const placeLeft = currentStep!.placement === 'left' && !isMobileViewport;
	const placeBelow = currentStep!.placement !== 'above' && currentStep!.placement !== 'left';
	let tooltipStyle: React.CSSProperties;
	// Honor the configured placement directly rather than flipping based on available space — `locateAnchor`
	// already scrolls to reserve room on the correct side, and flipping here caused 'above' steps to render
	// below when the anchor itself is taller than the viewport (e.g. a long timeline card).
	const showBelow = placeBelow;

	let left = cutCenterX - tooltipWidth / 2;
	let pointerLeft = 0;
	const cutCenterY = cutTop + cutHeight / 2;

	// A tall anchor (e.g. the mobile care-team or timeline card, which can be as tall as the screen)
	// leaves no room to sit fully outside it. Detect that here and overlay the tooltip against the
	// viewport edge with the largest gap instead of trying to squeeze it beside the anchor — squeezing
	// is what made the card and the tooltip visually collide.
	const measuredTooltipHeight = dialogRef.current?.offsetHeight ?? 0;
	const neededHeight = (measuredTooltipHeight || 260) + TOOLTIP_GAP + POINTER_HEIGHT + VIEWPORT_PADDING;
	const spaceAbove = cutTop;
	const spaceBelow = window.innerHeight - (cutTop + cutHeight);
	const mustOverlayAnchor = !placeLeft && spaceAbove < neededHeight && spaceBelow < neededHeight;

	if (mustOverlayAnchor) {
		// Pin to whichever edge has more room, so the tooltip stays fully on screen and reads as a
		// sheet over the highlighted card rather than a broken pointer tooltip.
		const maxLeftOverlay = window.innerWidth - tooltipWidth - VIEWPORT_PADDING;
		if (left > maxLeftOverlay) left = Math.max(VIEWPORT_PADDING, maxLeftOverlay);
		if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
		tooltipStyle = spaceBelow >= spaceAbove
			? { bottom: VIEWPORT_PADDING, left, width: tooltipWidth, boxShadow: TOOLTIP_SHADOW }
			: { top: VIEWPORT_PADDING, left, width: tooltipWidth, boxShadow: TOOLTIP_SHADOW };
	} else if (placeLeft) {
		// Tooltip beside the anchor's left edge, vertically centered on the anchor.
		const horizontalGap = TOOLTIP_GAP + POINTER_HEIGHT;
		left = Math.max(cutLeft - horizontalGap - tooltipWidth, VIEWPORT_PADDING);
		tooltipStyle = { top: cutCenterY, left, width: tooltipWidth, transform: 'translateY(-50%)', boxShadow: TOOLTIP_SHADOW };
	} else {
		const maxLeft = window.innerWidth - tooltipWidth - VIEWPORT_PADDING;
		if (left > maxLeft) left = Math.max(VIEWPORT_PADDING, maxLeft);
		if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;

		const verticalGap = TOOLTIP_GAP + POINTER_HEIGHT;
		if (showBelow) {
			tooltipStyle = { top: cutTop + cutHeight + verticalGap, left, width: tooltipWidth, boxShadow: TOOLTIP_SHADOW };
		} else {
			tooltipStyle = { bottom: window.innerHeight - cutTop + verticalGap, left, width: tooltipWidth, boxShadow: TOOLTIP_SHADOW };
		}
		pointerLeft = Math.min(Math.max(cutCenterX - left, 20), tooltipWidth - 20);
	}

	tooltipStyleRef.current = tooltipStyle;
	overlayModeRef.current = mustOverlayAnchor;

	// Carve the spotlight rectangle out of the blur layer so only the background blurs, never the anchor.
	const cutRight = cutLeft + cutWidth;
	const cutBottom = cutTop + cutHeight;
	const blurClipPath = `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${cutTop}px, ${cutLeft}px ${cutTop}px, ${cutLeft}px ${cutBottom}px, ${cutRight}px ${cutBottom}px, ${cutRight}px ${cutTop}px, 0 ${cutTop}px)`;

	const title = t(language, currentStep!.titleKey);
	const body = t(language, currentStep!.bodyKey);
	const currentCount = stepIndex + 1;
	const isFirstStep = stepIndex === 0;

	// Circular progress ring counter (Figma: current bold-black, /total faded, arc shows progress).
	const RING_SIZE = 34;
	const RING_STROKE = 2;
	const ringRadius = (RING_SIZE - RING_STROKE) / 2;
	const ringCircumference = 2 * Math.PI * ringRadius;
	const ringProgress = totalSteps > 0 ? currentCount / totalSteps : 0;

	return createPortal(
		<div className={backdropBase}>
			<div
				className="absolute inset-0"
				style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', clipPath: blurClipPath }}
			/>
			<div
				className="absolute pointer-events-none rounded-[8px] border-2 border-solid border-[#269c6e]"
				style={{ top: cutTop, left: cutLeft, width: cutWidth, height: cutHeight, boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' }}
			/>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-label={title}
				tabIndex={-1}
				className="absolute bg-white rounded-[12px] p-5 sm:p-6 flex flex-col gap-y-5 sm:gap-y-8"
				style={tooltipStyle}
			>
				{/* In overlay mode the tooltip sits on top of the highlighted card, so a pointer would aim
				    at the element underneath it and read as a rendering glitch. */}
				{!mustOverlayAnchor && <div
					className="absolute w-0 h-0"
					style={
						placeLeft
							? {
									// Pointer on the tooltip's right edge, vertically centered.
									right: -POINTER_HEIGHT,
									top: '50%',
									transform: 'translateY(-50%)',
									borderTop: '8px solid transparent',
									borderBottom: '8px solid transparent',
									borderLeft: `${POINTER_HEIGHT}px solid #ffffff`,
								}
							: showBelow
								? {
										top: -POINTER_HEIGHT,
										left: pointerLeft,
										transform: 'translateX(-50%)',
										borderLeft: '8px solid transparent',
										borderRight: '8px solid transparent',
										borderBottom: `${POINTER_HEIGHT}px solid #ffffff`,
									}
								: {
										bottom: -POINTER_HEIGHT,
										left: pointerLeft,
										transform: 'translateX(-50%)',
										borderLeft: '8px solid transparent',
										borderRight: '8px solid transparent',
										borderTop: `${POINTER_HEIGHT}px solid #ffffff`,
									}
					}
				/>}
				<div className="flex flex-col gap-y-4 sm:gap-y-6">
					<div className="flex items-start justify-between gap-x-2">
						<p className="text-[20px] sm:text-[24px] font-semibold text-black leading-[1.3]">{title}</p>
						<button
							type="button"
							aria-label={t(language, 'CareProfile/Tour/Back')}
							onClick={onSkip}
							className="flex items-center justify-center w-[31px] h-[31px] rounded-full bg-transparent border-none text-[#051b37] cursor-pointer flex-shrink-0 -mt-1 -mr-1"
						>
							<FiX size={19} />
						</button>
					</div>
					<p className="text-sm text-[#616975] leading-[21px]">{body}</p>
				</div>
				<div className="flex items-center justify-between gap-x-3">
					<span
						aria-live="polite"
						aria-label={t(language, 'CareProfile/Tour/Counter')
							.replace('{{current}}', String(currentCount))
							.replace('{{total}}', String(totalSteps))}
						className="relative flex items-center justify-center flex-shrink-0"
						style={{ width: RING_SIZE, height: RING_SIZE }}
					>
						<svg width={RING_SIZE} height={RING_SIZE} className="absolute inset-0 -rotate-90" aria-hidden="true">
							<circle
								cx={RING_SIZE / 2}
								cy={RING_SIZE / 2}
								r={ringRadius}
								fill="none"
								stroke="#eceef2"
								strokeWidth={RING_STROKE}
							/>
							<circle
								cx={RING_SIZE / 2}
								cy={RING_SIZE / 2}
								r={ringRadius}
								fill="none"
								stroke="#051b37"
								strokeWidth={RING_STROKE}
								strokeLinecap="round"
								strokeDasharray={ringCircumference}
								strokeDashoffset={ringCircumference * (1 - ringProgress)}
							/>
						</svg>
						<span className="text-[12px] font-medium leading-none" aria-hidden="true">
							<span className="text-black">{currentCount}</span>
							<span className="text-black opacity-40">/{totalSteps}</span>
						</span>
					</span>
					<div className="flex items-center gap-x-2">
						<button
							type="button"
							onClick={onBack}
							disabled={isFirstStep}
							className="text-sm font-medium text-[#616975] bg-white border border-solid border-[#dbdfe6] py-2.5 px-6 rounded-[16px] cursor-pointer disabled:opacity-40 disabled:cursor-default"
						>
							{t(language, 'CareProfile/Tour/Back')}
						</button>
						<button
							type="button"
							onClick={onNext}
							className="bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover text-white text-sm font-medium py-2.5 px-6 rounded-[16px] border-none cursor-pointer transition-all duration-200"
						>
							{t(language, 'CareProfile/Tour/Next')}
						</button>
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
};

export default CareProfileTour;
